// src/components/Layout/BottomNav.js
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useCall } from '../../contexts/CallContext';
import { db } from '../../services/firebase';
import { ref, onValue, get } from 'firebase/database';
import { setFaviconBadge } from '../../utils/faviconBadge';
import './BottomNav.css';

const SUPPORT_UID = '5cSplOLz30d8dgM3wvXUcDmL2QK2';
const WAVE_VIDEO = '/wave-bg.mp4';

const haptic = (pattern = 12) => {
  try {
    if (navigator.vibrate) navigator.vibrate(pattern);
  } catch (_) {}
};

const isOnlineValue = (val) => {
  if (!val) return false;
  if (val === 'online' || val === true) return true;
  if (typeof val === 'string') return val.toLowerCase() === 'online';
  if (typeof val === 'object') {
    if (val.state === 'online' || val.status === 'online' || val.isOnline === true) return true;
    if (val.lastChanged || val.last_seen || val.timestamp) {
      const ts = Number(val.lastChanged || val.last_seen || val.timestamp);
      if (ts > 1000000000000) return Date.now() - ts < 2 * 60 * 1000;
      if (ts > 1000000000) return Date.now() - ts * 1000 < 2 * 60 * 1000;
    }
  }
  return false;
};

const LiquidBlob = ({ targetX, targetW, dragging, dragX }) => {
  const blobRef = useRef(null);
  const pos = useRef(0);
  const vel = useRef(0);
  const w = useRef(0);
  const wVel = useRef(0);
  const stretch = useRef(0);
  const initialized = useRef(false);

  const STIFFNESS = 0.16;
  const DAMPING = 0.78;
  const W_STIFFNESS = 0.14;
  const W_DAMPING = 0.75;
  const STRETCH_FACTOR = 0.35;
  const MAX_STRETCH = 40;

  useEffect(() => {
    if (!initialized.current && Number.isFinite(targetX) && targetX > 0) {
      pos.current = targetX;
      w.current = targetW;
      initialized.current = true;
    }
  }, [targetX, targetW]);

  useEffect(() => {
    let raf = null;
    let last = performance.now();

    const tick = (now) => {
      const dt = Math.min(32, now - last) / 16.67;
      last = now;

      const validTarget = Number.isFinite(targetX) && targetX > 0;
      const target = validTarget
        ? dragging && dragX != null
          ? dragX
          : targetX
        : pos.current; 

      const targetWidth = validTarget ? targetW : w.current;

      const dx = target - pos.current;
      const forceX = dx * STIFFNESS;
      vel.current = (vel.current + forceX) * DAMPING;
      pos.current += vel.current * dt;

      const dw = targetWidth - w.current;
      const forceW = dw * W_STIFFNESS;
      wVel.current = (wVel.current + forceW) * W_DAMPING;
      w.current += wVel.current * dt;

      const vAbs = Math.abs(vel.current);
      const desiredStretch = Math.min(MAX_STRETCH, vAbs * STRETCH_FACTOR);
      stretch.current += (desiredStretch - stretch.current) * 0.3;

      const finalW = Math.max(w.current, targetWidth * 0.8) + stretch.current * 2;
      const finalH = 56 - stretch.current * 0.15;

      if (blobRef.current) {
        const el = blobRef.current;
        if (!validTarget && !initialized.current) {
          el.style.opacity = '0';
        } else {
          el.style.opacity = '1';
          el.style.transform = `translate3d(${pos.current - finalW / 2}px, 0, 0)`;
          el.style.width = `${finalW}px`;
          el.style.height = `${Math.max(48, finalH)}px`;
          const r = 22 - stretch.current * 0.08;
          el.style.borderRadius = `${Math.max(14, r)}px`;
        }
      }

      raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [targetX, targetW, dragging, dragX]);

  return (
    <div
      ref={blobRef}
      className="echo-v-blob"
      style={{ opacity: 0 }}
      aria-hidden="true"
    />
  );
};

const BottomNav = () => {
  const { user } = useAuth();
  const { startVideoCall } = useCall();
  const location = useLocation();
  const navigate = useNavigate();

  const [unreadChatsCount, setUnreadChatsCount] = useState(0);
  const [lastChatPreview, setLastChatPreview] = useState('');
  const [communityUnread, setCommunityUnread] = useState(0);
  const [controlUnread, setControlUnread] = useState(0);
  const [onlineCount, setOnlineCount] = useState(0);
  const [swingKey, setSwingKey] = useState(0);
  const [hidden, setHidden] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [pulseFlash, setPulseFlash] = useState(false);
  const [onlineSheetOpen, setOnlineSheetOpen] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [onlineLoading, setOnlineLoading] = useState(false);
  const [callingUid, setCallingUid] = useState(null);
  const [leavingId, setLeavingId] = useState(null);
  const [onlineError, setOnlineError] = useState('');

  const [blobTargetX, setBlobTargetX] = useState(0);
  const [blobTargetW, setBlobTargetW] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [dragX, setDragX] = useState(null);

  const swipeStartX = useRef(null);
  const swipeStartY = useRef(null);
  const swiping = useRef(false);
  const centerTouchY = useRef(null);
  const lastTapRef = useRef(0);
  const lastScrollY = useRef(0);
  const prevUnread = useRef(0);
  const statusRef = useRef({});
  const presenceRef = useRef({});
  const contactIdsRef = useRef([]);
  const videoRef = useRef(null);
  const navRef = useRef(null);
  const tabRefs = useRef({});

  const isSupport = user?.uid === SUPPORT_UID;

  const baseTabs = useMemo(() => {
    if (isSupport) {
      return [
        { to: '/', label: 'Home', icon: 'fa-house', id: 'home' },
        { to: '/control', label: 'Control', icon: 'fa-sliders-h', id: 'control' },
        { to: '/chats', label: 'Chats', icon: 'fa-comment-dots', id: 'chats' },
        { to: '/spotlight', label: 'Spotlight', icon: 'fa-bolt', id: 'spotlight' },
        { to: '/communities', label: 'Communities', icon: 'fa-users', id: 'communities' },
        { to: '/other', label: 'Other', icon: 'fa-ellipsis-h', id: 'other' },
      ];
    }
    return [
      { to: '/', label: 'Home', icon: 'fa-house', id: 'home' },
      { to: '/chats', label: 'Chats', icon: 'fa-comment-dots', id: 'chats' },
      { to: '/spotlight', label: 'Spotlight', icon: 'fa-bolt', id: 'spotlight' },
      { to: '/communities', label: 'Communities', icon: 'fa-users', id: 'communities' },
      { to: '/other', label: 'Other', icon: 'fa-ellipsis-h', id: 'other' },
    ];
  }, [isSupport]);

  const orderedTabs = baseTabs;
  const activeIndex = useMemo(() => {
    const idx = orderedTabs.findIndex((t) => {
      if (t.to === '/') return location.pathname === '/';
      return location.pathname.startsWith(t.to);
    });
    return idx >= 0 ? idx : 0;
  }, [location.pathname, orderedTabs]);

  const activeTab = orderedTabs[activeIndex];
  const sideTabs = orderedTabs.filter((_, i) => i !== activeIndex);
  const leftTabs = sideTabs.slice(0, Math.ceil(sideTabs.length / 2));
  const rightTabs = sideTabs.slice(Math.ceil(sideTabs.length / 2));
  const hasPulse = unreadChatsCount > 0 || onlineCount > 0;

  const computeTarget = useCallback(() => {
    const el = tabRefs.current[activeTab?.id];
    const parent = navRef.current;
    if (!el || !parent) return;
    const elRect = el.getBoundingClientRect();
    const parentRect = parent.getBoundingClientRect();

    if (elRect.width === 0 || parentRect.width === 0) return;

    const nextX = elRect.left - parentRect.left + elRect.width / 2;
    const nextW = elRect.width;

    setBlobTargetX((prev) => (Math.abs(prev - nextX) < 0.5 ? prev : nextX));
    setBlobTargetW((prev) => (Math.abs(prev - nextW) < 0.5 ? prev : nextW));
  }, [activeTab?.id]);

  useEffect(() => {
    const raf = requestAnimationFrame(() => {
      requestAnimationFrame(() => computeTarget());
    });
    return () => cancelAnimationFrame(raf);
  }, [computeTarget, location.pathname, activeIndex]);

  useEffect(() => {
    if (!navRef.current) return;
    const observer = new ResizeObserver(() => computeTarget());
    observer.observe(navRef.current);
    return () => observer.disconnect();
  }, [computeTarget]);

  useEffect(() => {
    const onResize = () => computeTarget();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [computeTarget]);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    const play = () => el.play().catch(() => {});
    play();
    el.addEventListener('canplay', play);
    return () => el.removeEventListener('canplay', play);
  }, []);

  useEffect(() => setSwingKey((k) => k + 1), [activeIndex]);

  useEffect(() => {
    if (unreadChatsCount > prevUnread.current) {
      setPulseFlash(true);
      haptic([10, 30, 10]);
      const t = setTimeout(() => setPulseFlash(false), 1600);
      prevUnread.current = unreadChatsCount;
      return () => clearTimeout(t);
    }
    prevUnread.current = unreadChatsCount;
  }, [unreadChatsCount]);

  useEffect(() => {
    const el = document.getElementById('main-content') || window;
    const getY = () => (el === window ? window.scrollY : el.scrollTop || 0);
    lastScrollY.current = getY();
    const onScroll = () => {
      const y = getY();
      const delta = y - lastScrollY.current;
      if (y > 50 && delta > 6) setHidden(true);
      else if (delta < -5 || y < 30) setHidden(false);
      lastScrollY.current = y;
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setSheetOpen(false);
    setOnlineSheetOpen(false);
  }, [location.pathname]);

  const [liveHidden, setLiveHidden] = useState(
    typeof document !== 'undefined' && document.body.classList.contains('echo-hide-bottom-nav')
  );

  useEffect(() => {
    const check = () => {
      const bodyHide = document.body.classList.contains('echo-hide-bottom-nav');
      const liveUi = Boolean(
        document.querySelector(
          '.broadcast-view, .live-view-modal, .LiveViewModal, [data-echo-live="open"]'
        )
      );
      setLiveHidden(bodyHide || liveUi);
    };
    check();
    const mo = new MutationObserver(check);
    mo.observe(document.body, {
      attributes: true,
      attributeFilter: ['class'],
      childList: true,
      subtree: true,
    });
    return () => mo.disconnect();
  }, []);

  useEffect(() => {
    if (liveHidden) {
      setBlobTargetX(0);
      setBlobTargetW(0);
      setDragging(false);
      setDragX(null);
    }
  }, [liveHidden]);

  const recountOnline = useCallback(() => {
    const me = user?.uid;
    const ids = new Set();
    Object.entries(statusRef.current).forEach(([uid, val]) => {
      if (uid !== me && isOnlineValue(val)) ids.add(uid);
    });
    Object.entries(presenceRef.current).forEach(([uid, val]) => {
      if (uid !== me && isOnlineValue(val)) ids.add(uid);
    });
    setOnlineCount(ids.size);
  }, [user?.uid]);

  useEffect(() => {
    const unsubs = [];
    unsubs.push(
      onValue(ref(db, 'status'), (snap) => {
        statusRef.current = snap.val() || {};
        recountOnline();
      })
    );
    unsubs.push(
      onValue(ref(db, 'presence'), (snap) => {
        presenceRef.current = snap.val() || {};
        recountOnline();
      })
    );
    return () => unsubs.forEach((u) => typeof u === 'function' && u());
  }, [recountOnline]);

  useEffect(() => {
    if (!user?.uid) {
      contactIdsRef.current = [];
      setUnreadChatsCount(0);
      setLastChatPreview('');
      return;
    }
    return onValue(ref(db, `userChats/${user.uid}`), (snapshot) => {
      const data = snapshot.val() || {};
      const ids = [];
      Object.entries(data).forEach(([key, chat]) => {
        const other =
          chat.userId ||
          chat.uid ||
          chat.partnerId ||
          chat.receiverId ||
          chat.senderId ||
          (key !== user.uid ? key : null);
        if (other && other !== user.uid && other !== 'echo_ai_assistant') {
          ids.push(String(other));
        }
      });
      contactIdsRef.current = [...new Set(ids)];
      let count = 0;
      let latest = null;
      Object.values(data).forEach((chat) => {
        if (chat.unreadCount > 0) count++;
        const ts = chat.lastMessageTime || chat.timestamp || 0;
        if (!latest || ts > latest.ts) {
          latest = {
            ts,
            text: chat.lastMessage || chat.lastMessageText || '',
            name: chat.userName || chat.name || 'Chat',
          };
        }
      });
      setUnreadChatsCount(count);
      setFaviconBadge(count).catch(() => {});
      setLastChatPreview(latest?.text ? `${latest.name}: ${latest.text}` : '');
    });
  }, [user?.uid]);

  const fetchProfile = async (uid) => {
    const paths = [`users/${uid}`, `profiles/${uid}`, `userProfiles/${uid}`];
    for (const p of paths) {
      try {
        const snap = await get(ref(db, p));
        if (snap.exists()) {
          const v = snap.val();
          return {
            uid,
            name: v.name || v.displayName || v.username || v.userName || 'User',
            avatar: v.avatar || v.photoURL || v.profileImage || v.image || '',
          };
        }
      } catch (_) {}
    }
    return { uid, name: 'User', avatar: '' };
  };

  const collectOnlineUids = () => {
    const me = user?.uid;
    const online = new Set();
    Object.entries(statusRef.current).forEach(([uid, val]) => {
      if (uid !== me && isOnlineValue(val)) online.add(uid);
    });
    Object.entries(presenceRef.current).forEach(([uid, val]) => {
      if (uid !== me && isOnlineValue(val)) online.add(uid);
    });
    return online;
  };

  const openOnlineSheet = async () => {
    setSheetOpen(false);
    setOnlineSheetOpen(true);
    setOnlineLoading(true);
    setOnlineError('');
    haptic(14);
    try {
      const onlineSet = collectOnlineUids();
      const contacts = contactIdsRef.current || [];
      let uids = contacts.filter((id) => onlineSet.has(id));
      if (uids.length === 0) uids = [...onlineSet];
      uids = uids.slice(0, 50);
      const verified = [];
      for (const uid of uids) {
        let live = onlineSet.has(uid);
        if (!live) {
          try {
            const s1 = await get(ref(db, `status/${uid}`));
            const s2 = await get(ref(db, `presence/${uid}`));
            live = isOnlineValue(s1.val()) || isOnlineValue(s2.val());
          } catch (_) {}
        }
        if (live) verified.push(uid);
      }
      const profiles = await Promise.all(verified.map((uid) => fetchProfile(uid)));
      const contactSet = new Set(contacts);
      profiles.sort((a, b) => {
        const ac = contactSet.has(a.uid) ? 0 : 1;
        const bc = contactSet.has(b.uid) ? 0 : 1;
        if (ac !== bc) return ac - bc;
        return String(a.name).localeCompare(String(b.name));
      });
      setOnlineUsers(profiles);
      setOnlineCount(profiles.length);
      if (profiles.length === 0) setOnlineError('No one is online on ECHO right now');
    } catch (err) {
      console.error('Online list failed:', err);
      setOnlineUsers([]);
      setOnlineError('Could not load online users');
    } finally {
      setOnlineLoading(false);
    }
  };

  const callUser = async (person) => {
    if (!person?.uid) return;
    if (!startVideoCall) {
      setOnlineError('Calling is not available right now');
      return;
    }
    let stillOnline = false;
    try {
      const s1 = await get(ref(db, `status/${person.uid}`));
      const s2 = await get(ref(db, `presence/${person.uid}`));
      stillOnline = isOnlineValue(s1.val()) || isOnlineValue(s2.val());
    } catch (_) {
      stillOnline = true;
    }
    if (!stillOnline) {
      setOnlineError(`${person.name} just went offline`);
      setOnlineUsers((prev) => prev.filter((p) => p.uid !== person.uid));
      haptic([20, 40]);
      return;
    }
    setCallingUid(person.uid);
    haptic([12, 24, 12]);
    try {
      await Promise.resolve(startVideoCall(person.uid, person.name, person.avatar || ''));
      setOnlineSheetOpen(false);
    } catch (err) {
      console.error('Call failed:', err);
      setOnlineError('Could not start the call');
    } finally {
      setTimeout(() => setCallingUid(null), 900);
    }
  };

  useEffect(() => {
    if (!user?.uid) return setCommunityUnread(0);
    return onValue(ref(db, `userCommunityUnread/${user.uid}`), (snap) => {
      const val = snap.val();
      if (typeof val === 'number') setCommunityUnread(val);
      else if (val && typeof val === 'object') {
        setCommunityUnread(Object.values(val).reduce((a, b) => a + (Number(b) || 0), 0));
      } else setCommunityUnread(0);
    });
  }, [user?.uid]);

  useEffect(() => {
    if (!user?.uid || !isSupport) return setControlUnread(0);
    return onValue(ref(db, `adminNotifications/${user.uid}/messages`), (snap) => {
      const data = snap.val();
      setControlUnread(data ? Object.values(data).filter((m) => !m.read).length : 0);
    });
  }, [user?.uid, isSupport]);

  const getBadge = (tab) => {
    if (tab.id === 'chats' && unreadChatsCount > 0)
      return unreadChatsCount > 9 ? '9+' : String(unreadChatsCount);
    if (tab.id === 'communities' && communityUnread > 0)
      return communityUnread > 9 ? '9+' : String(communityUnread);
    if (tab.id === 'control' && controlUnread > 0)
      return controlUnread > 9 ? '9+' : String(controlUnread);
    return null;
  };

  const goToTab = (tab) => {
    if (!tab) return;
    const same =
      tab.to === '/' ? location.pathname === '/' : location.pathname.startsWith(tab.to);
    if (same) return;
    haptic(12);
    setSheetOpen(false);
    setOnlineSheetOpen(false);
    setHidden(false);
    navigate(tab.to);
  };

  const goRelative = (dir) => {
    const next = orderedTabs[(activeIndex + dir + orderedTabs.length) % orderedTabs.length];
    goToTab(next);
  };

  const scrollToTop = () => {
    const el = document.getElementById('main-content');
    if (el) el.scrollTo({ top: 0, behavior: 'smooth' });
    else window.scrollTo({ top: 0, behavior: 'smooth' });
    haptic(8);
  };

  const onTabClick = (tab, isCenter = false) => {
    if (swiping.current) return;
    if (isCenter) {
      const now = Date.now();
      if (now - lastTapRef.current < 280) {
        lastTapRef.current = 0;
        scrollToTop();
        return;
      }
      lastTapRef.current = now;
      goToTab(tab);
      return;
    }
    setLeavingId(tab.id);
    setTimeout(() => {
      setLeavingId(null);
      goToTab(tab);
    }, 180);
  };

  const onNavPointerDown = (e) => {
    const t = e.touches ? e.touches[0] : e;
    swipeStartX.current = t.clientX;
    swipeStartY.current = t.clientY;
    swiping.current = false;
  };

  const onNavPointerMove = (e) => {
    if (swipeStartX.current == null) return;
    const t = e.touches ? e.touches[0] : e;
    const dx = t.clientX - swipeStartX.current;
    const dy = t.clientY - (swipeStartY.current ?? t.clientY);
    if (Math.abs(dx) > 12 && Math.abs(dx) > Math.abs(dy)) {
      swiping.current = true;
      const parent = navRef.current;
      if (parent) {
        const parentRect = parent.getBoundingClientRect();
        const localX = t.clientX - parentRect.left;
        setDragging(true);
        setDragX(localX);
      }
    }
  };

  const onNavPointerUp = (e) => {
    if (swipeStartX.current == null) return;
    const t = e.changedTouches ? e.changedTouches[0] : e;
    const dx = t.clientX - swipeStartX.current;
    const dy = t.clientY - (swipeStartY.current ?? t.clientY);
    swipeStartX.current = null;
    swipeStartY.current = null;
    setDragging(false);
    setDragX(null);
    if (Math.abs(dx) >= 50 && Math.abs(dx) > Math.abs(dy)) {
      swiping.current = true;
      if (dx < 0) goRelative(1);
      else goRelative(-1);
      setTimeout(() => {
        swiping.current = false;
      }, 200);
      return;
    }
    setTimeout(() => {
      swiping.current = false;
    }, 80);
  };

  const onCenterTouchStart = (e) => {
    centerTouchY.current = e.touches?.[0]?.clientY ?? null;
  };

  const onCenterTouchEnd = (e) => {
    if (centerTouchY.current == null) return;
    const y = e.changedTouches?.[0]?.clientY;
    const dy = centerTouchY.current - y;
    centerTouchY.current = null;
    if (dy > 36 && !swiping.current) {
      haptic(14);
      setSheetOpen(true);
    }
  };

  const initials = (name) => {
    const p = String(name || 'U').trim().split(/\s+/);
    return ((p[0]?.[0] || 'U') + (p[1]?.[0] || '')).toUpperCase();
  };

  const renderSide = (tab) => {
    const badge = getBadge(tab);
    return (
      <button
        key={tab.id}
        ref={(el) => { tabRefs.current[tab.id] = el; }}
        type="button"
        id={`nav-${tab.id}`}
        className={`echo-v-tab ${leavingId === tab.id ? 'is-leaving' : ''}`}
        onClick={() => onTabClick(tab)}
      >
        <div className="echo-v-icon">
          <i className={`fas ${tab.icon}`} />
          {badge && <span className="echo-v-badge">{badge}</span>}
        </div>
        <span className="echo-v-label">{tab.label}</span>
      </button>
    );
  };

  if (liveHidden) return null;

  return (
    <nav
      ref={navRef}
      className={`echo-v-nav ${hidden ? 'is-hidden' : ''} ${liveHidden ? 'is-live-hidden' : ''}`}
      aria-label="Bottom navigation"
      onTouchStart={onNavPointerDown}
      onTouchMove={onNavPointerMove}
      onTouchEnd={onNavPointerUp}
      onMouseDown={onNavPointerDown}
      onMouseUp={onNavPointerUp}
    >
      <div className="echo-v-video" aria-hidden="true">
        <video ref={videoRef} src={WAVE_VIDEO} autoPlay muted loop playsInline preload="auto" />
      </div>

      <LiquidBlob
        targetX={blobTargetX}
        targetW={blobTargetW}
        dragging={dragging}
        dragX={dragX}
      />

      {previewOpen && lastChatPreview && (
        <div className="echo-v-preview" onClick={() => setPreviewOpen(false)}>
          <span className="dot" />
          <span>{lastChatPreview}</span>
        </div>
      )}

      {onlineSheetOpen && (
        <div className="echo-online-wrap">
          <div className="echo-online-bg" onClick={() => setOnlineSheetOpen(false)} />
          <div className="echo-online-sheet">
            <div className="handle" />
            <div className="online-head">
              <div>
                <h3>Who's online</h3>
                <p>
                  {onlineLoading
                    ? 'Connecting to presence…'
                    : `${onlineUsers.length} online on ECHO`}
                </p>
              </div>
              <button
                type="button"
                className="online-close"
                onClick={() => setOnlineSheetOpen(false)}
              >
                <i className="fas fa-xmark" />
              </button>
            </div>
            {onlineError && <div className="online-error">{onlineError}</div>}
            <div className="online-list">
              {onlineLoading && <div className="online-empty">Finding people online…</div>}
              {!onlineLoading && onlineUsers.length === 0 && (
                <div className="online-empty">
                  <i className="fas fa-moon" style={{ marginBottom: 8, opacity: 0.5 }} />
                  <div>No one online right now</div>
                </div>
              )}
              {!onlineLoading &&
                onlineUsers.map((person) => (
                  <button
                    key={person.uid}
                    type="button"
                    className={`online-row ${callingUid === person.uid ? 'calling' : ''}`}
                    onClick={() => callUser(person)}
                  >
                    <div className="online-avatar">
                      {person.avatar ? (
                        <img
                          src={person.avatar}
                          alt=""
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      ) : (
                        <span>{initials(person.name)}</span>
                      )}
                      <i className="online-dot" />
                    </div>
                    <div className="online-meta">
                      <span className="online-name">{person.name}</span>
                      <span className="online-sub">
                        {callingUid === person.uid ? 'Calling…' : 'Online · Tap to video call'}
                      </span>
                    </div>
                    <span className="online-call-btn">
                      <i
                        className={`fas ${
                          callingUid === person.uid ? 'fa-spinner fa-spin' : 'fa-video'
                        }`}
                      />
                    </span>
                  </button>
                ))}
            </div>
          </div>
        </div>
      )}

      {sheetOpen && (
        <div className="echo-v-sheet-wrap">
          <div className="echo-v-sheet-bg" onClick={() => setSheetOpen(false)} />
          <div className="echo-v-sheet">
            <div className="handle" />
            <p>Quick actions</p>
            <div className="grid">
              {[
                { label: 'Chats', icon: 'fa-comment-dots', to: '/chats' },
                { label: 'Spotlight', icon: 'fa-bolt', to: '/spotlight' },
                { label: 'Communities', icon: 'fa-users', to: '/communities' },
                { label: 'Online', icon: 'fa-video', action: openOnlineSheet },
              ].map((a) => (
                <button
                  key={a.label}
                  type="button"
                  onClick={() => {
                    setSheetOpen(false);
                    if (a.action) a.action();
                    else {
                      navigate(a.to);
                      haptic(10);
                    }
                  }}
                >
                  <i className={`fas ${a.icon}`} />
                  {a.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="echo-v-inner">
        <div className="echo-v-side">{leftTabs.map(renderSide)}</div>
        <div className="echo-v-center">
          <button
            key={swingKey}
            type="button"
            id="nav-center"
            className={`echo-v-tab center ${hasPulse ? 'has-pulse' : ''} ${
              pulseFlash ? 'pulse-flash' : ''
            }`}
            onClick={() => onTabClick(activeTab, true)}
            onTouchStart={onCenterTouchStart}
            onTouchEnd={onCenterTouchEnd}
            aria-current="page"
          >
            <div className="echo-v-icon center-icon">
              <i className={`fas ${activeTab.icon}`} />
              {getBadge(activeTab) && (
                <span
                  className="echo-v-badge"
                  onClick={(e) => {
                    if (activeTab.id === 'chats' && lastChatPreview) {
                      e.stopPropagation();
                      setPreviewOpen((p) => !p);
                    }
                  }}
                >
                  {getBadge(activeTab)}
                </span>
              )}
            </div>
            <span className="echo-v-label">{activeTab.label}</span>
          </button>
        </div>
        <div className="echo-v-side">{rightTabs.map(renderSide)}</div>
      </div>
    </nav>
  );
};

export default BottomNav;  
